import { useState } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'

import { V1_TVL_DEAL_TYPE } from '../../../lib/constants'
import type { DealFormData } from '../../../lib/types'
import { validateV1Deposit } from '../../../lib/step-validators'
import { Button } from '../../ui/button'
import {
  V1DepositFields,
  getV1DepositFieldLabel,
  pickV1DepositFields,
  type V1DepositFieldData,
  type V1DepositFieldKey,
  type V1DepositListPatch,
} from './v1-deposit-fields'

interface StepV1DepositProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate: (data: Partial<DealFormData>) => void
  onBack: () => void
  onSubmitDeal?: (data?: Partial<DealFormData>) => void
  isSubmitting?: boolean
  submitLabel?: string
  inProgressLabel?: string
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
  requestChanges?: Record<string, string>
}

/** Timeline "NFT Transferable" is stored via the custom-date path. */
const withTransferability = (data: V1DepositFieldData) => ({
  ...data,
  nftTransferability: data.customStartDateTime ? 'custom' : 'never',
})

export function StepV1Deposit({
  formData,
  onComplete,
  onUpdate,
  onBack,
  onSubmitDeal,
  isSubmitting = false,
  submitLabel = 'Submit',
  inProgressLabel = 'Submitting...',
  onValidationErrors,
  requestChanges,
}: StepV1DepositProps) {
  const [localData, setLocalData] = useState(() =>
    pickV1DepositFields(formData),
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const isTvl = formData.salesType === V1_TVL_DEAL_TYPE

  const updateLists = (patch: V1DepositListPatch) => {
    const updated = { ...localData, ...patch }
    setLocalData(updated)
    onUpdate(withTransferability(updated) as Partial<DealFormData>)
    // Row errors are keyed by index, which shifts when a row is removed.
    setErrors((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(
          ([key]) => !Object.keys(patch).some((list) => key.startsWith(list)),
        ),
      ),
    )
  }

  const updateField = (field: V1DepositFieldKey, value: string) => {
    const updated = { ...localData, [field]: value }
    setLocalData(updated)
    onUpdate(withTransferability(updated) as Partial<DealFormData>)
    if (errors[field]) {
      setErrors((previous) => {
        const next = { ...previous }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = () => {
    const validation = validateV1Deposit(localData, { tvl: isTvl })
    setErrors(validation.errors)
    if (!validation.isValid) {
      onValidationErrors?.(
        Object.keys(validation.errors).map((id) => ({
          id,
          label: getV1DepositFieldLabel(id),
        })),
      )
      return
    }
    onValidationErrors?.([])
    const payload = withTransferability(localData) as Partial<DealFormData>
    onComplete(payload)
    onSubmitDeal?.(payload)
  }

  return (
    <div className="space-y-6">
      <V1DepositFields
        data={localData}
        errors={errors}
        requestChanges={requestChanges}
        onChange={updateField}
        onPatch={updateLists}
        tvl={isTvl}
      />

      <div className="flex justify-between pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          size="md"
          className="flex items-center gap-2 bg-transparent px-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {inProgressLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </div>
  )
}
